/* tslint:disable */
/* eslint-disable */

import * as runtime from '../runtime';
import { BaseAPI } from '../baseApi';
import {
  CreateStarGoalPayload,
  EditStarGoalPayload,
  StarBalance,
  StarGoal,
  StarStreak,
} from '../types';
import { HttpStatusCode } from 'axios';
import { ResponseError } from '../runtime';

/**
 * StarGoalApi — block 1: Stjärnmål (BARN-01..07) och barnets personliga stjärnsaldo.
 *
 * KONTRAKT, ej live-implementation. Endpoints/scheman definierade i BACKEND-KONTRAKT-NPF.md.
 * `supervisedTag` används precis som i ActivityApi: förälder/handledare anger barnets tag
 * för att agera för barnets räkning. Skrivoperationer på mål kräver förälderroll i backend.
 *
 * Stjärn-integritet: `currentStars` (StarGoal) och `availableStars` (StarBalance) härleds/bokförs
 * uteslutande i backend. Frontend läser dem men kan aldrig skriva dem.
 */
export type ListStarGoalsRequest = {
  supervisedTag?: string;
  // valfritt statusfilter; utelämnat => endast 'active'
  status?: StarGoal['status'];
};

export type GetStarBalanceRequest = {
  supervisedTag?: string;
};

export type GetStarStreakRequest = {
  supervisedTag?: string;
};

export type CreateStarGoalRequest = {
  payload: CreateStarGoalPayload;
  supervisedTag?: string; // förälder anger barnets tag
};

export type EditStarGoalRequest = {
  goalId: string;
  payload: EditStarGoalPayload;
  supervisedTag?: string;
};

export type RemoveStarGoalRequest = {
  goalId: string;
  supervisedTag?: string;
};

/**
 * Förälder bekräftar inlösen (BARN-06): målet arkiveras. currentStars nollställs inte.
 */
export type FulfillStarGoalRequest = {
  goalId: string;
  supervisedTag?: string;
};

/**
 * BARNET väljer vilket aktivt mål nästa tjänade stjärna ska räknas mot. Enda skriv-
 * operationen på mål som INTE kräver förälderroll — barnet väljer bland de mål föräldern
 * redan skapat. Backend svarar 404 om målet inte tillhör barnet eller inte är aktivt.
 */
export type SelectStarGoalRequest = {
  goalId: string;
  supervisedTag?: string; // valfritt: förälder kan välja för barnets räkning
};

export class StarGoalApi extends BaseAPI {
  // GET /star-goals?supervisedTag=&status=  -> alla mål för barnet
  listStarGoals = async (
    requestParameters: ListStarGoalsRequest,
  ): Promise<StarGoal[] | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.status !== undefined) {
      queryParameters['status'] = requestParameters.status;
    }
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/star-goals`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): StarGoal[] => jsonValue,
      ).value();
    } catch (error) {}
  };

  // GET /star-balance?supervisedTag=  -> barnets köpbara stjärnsaldo
  getStarBalance = async (
    requestParameters: GetStarBalanceRequest,
  ): Promise<StarBalance | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/star-balance`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): StarBalance => jsonValue,
      ).value();
    } catch (error) {}
  };

  // GET /star-streak?supervisedTag=  -> barnets dagsstreak (dagar i rad)
  getStarStreak = async (
    requestParameters: GetStarStreakRequest,
  ): Promise<StarStreak | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/star-streak`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): StarStreak => jsonValue,
      ).value();
    } catch (error) {}
  };

  // POST /star-goals  (förälder skapar mål)
  createStarGoal = async (
    requestParameters: CreateStarGoalRequest,
  ): Promise<HttpStatusCode | ResponseError | undefined> => {
    const queryParameters: any = {};
    const headerParameters: runtime.HTTPHeaders = { 'Content-Type': 'application/json' };
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/star-goals`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody(requestParameters.payload),
      });
      return response.status;
    } catch (error: unknown) {
      if (error instanceof ResponseError) return error;
    }
  };

  // PUT /star-goals/{goalId}  (förälder redigerar mål)
  editStarGoal = async (
    requestParameters: EditStarGoalRequest,
  ): Promise<HttpStatusCode | ResponseError | undefined> => {
    const queryParameters: any = {};
    const headerParameters: runtime.HTTPHeaders = { 'Content-Type': 'application/json' };
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/star-goals/${requestParameters.goalId}`,
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody(requestParameters.payload),
      });
      return response.status;
    } catch (error: unknown) {
      if (error instanceof ResponseError) return error;
    }
  };

  // DELETE /star-goals/{goalId}  (kopplade aktiviteter tappar goalId, raderas ej)
  removeStarGoal = async (
    requestParameters: RemoveStarGoalRequest,
  ): Promise<HttpStatusCode | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/star-goals/${requestParameters.goalId}`,
        method: 'DELETE',
        query: queryParameters,
      });
      return response.status;
    } catch (error) {}
  };

  // PUT /star-goals/{goalId}/fulfill  (förälder bekräftar att belöning är given)
  fulfillStarGoal = async (
    requestParameters: FulfillStarGoalRequest,
  ): Promise<HttpStatusCode | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/star-goals/${requestParameters.goalId}/fulfill`,
        method: 'PUT',
        query: queryParameters,
      });
      return response.status;
    } catch (error) {}
  };

  // PUT /star-goals/{goalId}/select  (barnet väljer vilket mål stjärnorna går till)
  selectStarGoal = async (
    requestParameters: SelectStarGoalRequest,
  ): Promise<HttpStatusCode | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/star-goals/${requestParameters.goalId}/select`,
        method: 'PUT',
        query: queryParameters,
      });
      return response.status;
    } catch (error) {}
  };
}
